import { useTranslation } from 'react-i18next';

export default function PrivacyPolicy({ onBack }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-page)] text-[var(--aif-text-primary)]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-[var(--aif-bg-surface)] transition"
              aria-label={t('onboarding.back')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h1 className="text-2xl font-bold">{t('privacy.title')}</h1>
        </div>

        <div className="prose dark:prose-invert max-w-none space-y-6">
          {/* 1. Verantwortlicher */}
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Verantwortlicher</h2>
            <p>
              ALCEDO Management GmbH<br />
              (Kontaktdaten gem. Impressum auf airfarm.io)
            </p>
            <p>
              Lizenznehmer f&uuml;r den DACH-Vertrieb: Airfarm GmbH
            </p>
          </section>

          {/* 2. Datenerhebung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">2. Welche Daten wir erheben</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Kontodaten:</strong> E-Mail-Adresse, Name, Passwort (gehasht), Sprache</li>
              <li><strong>Betriebsdaten:</strong> Betriebsname, Fl&auml;che, Region, Kulturen, Tierhaltung</li>
              <li><strong>Chatverl&auml;ufe:</strong> Ihre Nachrichten und KI-Antworten</li>
              <li><strong>Nutzungsdaten:</strong> Zeitstempel, aufgerufene Funktionen, Token-Verbrauch</li>
              <li><strong>Technische Daten:</strong> IP-Adresse, User-Agent, Ger&auml;tetyp</li>
              <li><strong>Zahlungsdaten:</strong> Werden ausschlie&szlig;lich bei Stripe Inc. verarbeitet</li>
            </ul>
          </section>

          {/* 3. Zweck der Verarbeitung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">3. Zweck der Verarbeitung</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Bereitstellung und Verbesserung des KI-CoPilot-Dienstes</li>
              <li>Personalisierte landwirtschaftliche Empfehlungen</li>
              <li>Abrechnung und Abonnementverwaltung</li>
              <li>Einhaltung gesetzlicher Pflichten (z.B. Aufbewahrungspflichten)</li>
              <li>Sicherheit und Missbrauchspr&auml;vention</li>
            </ul>
            <p className="mt-2">
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserf&uuml;llung),
              Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse),
              Art. 6 Abs. 1 lit. a DSGVO (Einwilligung f&uuml;r optionale Cookies).
            </p>
          </section>

          {/* 4. Drittanbieter */}
          <section>
            <h2 className="text-xl font-semibold mb-3">4. Drittanbieter und Auftragsverarbeiter</h2>

            <h3 className="text-lg font-medium mt-4 mb-2">Anthropic (Claude API)</h3>
            <p>
              Ihre Chat-Nachrichten werden zur Verarbeitung an die Anthropic API
              (Anthropic, PBC, San Francisco, USA) &uuml;bermittelt. Es gelten die{' '}
              Anthropic-Nutzungsbedingungen. Daten werden gem&auml;&szlig; EU-US Data Privacy
              Framework &uuml;bermittelt.
            </p>

            <h3 className="text-lg font-medium mt-4 mb-2">Deutscher Wetterdienst (DWD)</h3>
            <p>
              Wetterdaten werden vom DWD (MOSMIX-Dienst) abgerufen. Dabei werden
              keine personenbezogenen Daten an den DWD &uuml;bermittelt.
            </p>

            <h3 className="text-lg font-medium mt-4 mb-2">Stripe Inc.</h3>
            <p>
              Zahlungsabwicklung erfolgt &uuml;ber Stripe Inc. (Dublin, Irland).
              Kreditkartendaten werden ausschlie&szlig;lich von Stripe verarbeitet und
              nie auf unseren Servern gespeichert. Es gilt die Stripe-Datenschutzerkl&auml;rung.
            </p>

            <h3 className="text-lg font-medium mt-4 mb-2">Hetzner Online GmbH</h3>
            <p>
              Hosting auf Servern von Hetzner Online GmbH (Gunzenhausen, Deutschland).
              Alle Daten werden in Deutschland gespeichert.
            </p>
          </section>

          {/* 5. Speicherung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">5. Speicherdauer</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Kontodaten:</strong> Bis zur Kontol&ouml;schung</li>
              <li><strong>Chatverl&auml;ufe:</strong> Bis zur Kontol&ouml;schung oder manuellen L&ouml;schung</li>
              <li><strong>Abrechnungsdaten:</strong> 10 Jahre (gesetzliche Aufbewahrungspflicht)</li>
              <li><strong>Audit-Logs:</strong> 12 Monate</li>
              <li><strong>Anonymisierte Analysen:</strong> Unbegrenzt (keine Personenbezug)</li>
            </ul>
          </section>

          {/* 6. Ihre Rechte */}
          <section>
            <h2 className="text-xl font-semibold mb-3">6. Ihre Rechte</h2>
            <p>Sie haben folgende Rechte gem&auml;&szlig; DSGVO:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Auskunft (Art. 15):</strong> Export aller Ihrer Daten &uuml;ber Einstellungen &rarr; Datenschutz</li>
              <li><strong>L&ouml;schung (Art. 17):</strong> Konto l&ouml;schen &uuml;ber Einstellungen &rarr; Datenschutz (Anonymisierung)</li>
              <li><strong>Berichtigung (Art. 16):</strong> &Auml;ndern Sie Ihre Daten in den Profileinstellungen</li>
              <li><strong>Daten&uuml;bertragbarkeit (Art. 20):</strong> JSON-Export Ihrer Daten</li>
              <li><strong>Widerruf der Einwilligung (Art. 7):</strong> Jederzeit &uuml;ber Cookie-Einstellungen</li>
              <li><strong>Beschwerde:</strong> Bei der zust&auml;ndigen Datenschutzaufsichtsbeh&ouml;rde</li>
            </ul>
          </section>

          {/* 7. Cookies */}
          <section>
            <h2 className="text-xl font-semibold mb-3">7. Cookies</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Essenzielle Cookies:</strong> Authentifizierung, Spracheinstellung (immer aktiv)</li>
              <li><strong>Analyse-Cookies:</strong> Nutzungsstatistiken zur Verbesserung des Dienstes (optional)</li>
            </ul>
            <p className="mt-2">
              Sie k&ouml;nnen optionale Cookies jederzeit &uuml;ber das Cookie-Banner oder
              die Einstellungen ablehnen oder widerrufen.
            </p>
          </section>

          {/* 8. Kontakt */}
          <section>
            <h2 className="text-xl font-semibold mb-3">8. Kontakt</h2>
            <p>
              Bei Fragen zum Datenschutz wenden Sie sich an:<br />
              E-Mail: datenschutz@airfarm.io
            </p>
          </section>

          <p className="text-sm text-[var(--aif-text-muted)] mt-8">
            Stand: M&auml;rz 2026
          </p>
        </div>
      </div>
    </div>
  );
}
